import { h } from "vue";
import { createRouter, createWebHistory, RouterView } from "vue-router";
import axios from "axios";
import { JwtHelper } from "@/helpers/utilities/JwtHelper";
import { useSettingsStore } from "@/stores/settings";
import { useMeStore } from "@/stores/me";
import { SessionHelper } from "@/helpers/utilities/sessionHelper";

let i18n;

const RouterViewShell = { render: () => h(RouterView) };

export const nwpRouter = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: "/",
      name: "home",
      component: () => import("@/views/HomeView.vue"),
      props: true
    },
    {
      path: "/tiles/:groupId",
      name: "tiles",
      component: () => import("@/views/HomeView.vue"),
      props: true
    },

    // Colabs
    {
      path: "/colabs",
      component: RouterViewShell,
      children: [
        {
          path: "",
          name: "colabs",
          component: () => import("@/applications/nordicworkplace/nwp/views/customerSpecifics/sanovo/colabView.vue"),
          props: true
        },
        {
          path: ":colabId?/edit",
          name: "colab",
          component: () =>
            import("@/applications/nordicworkplace/nwp/views/customerSpecifics/sanovo/colabCreateView.vue"),
          props: route => ({
            colabId: route.params.colabId || undefined
          })
        },
        {
          path: "fromproject/:projectId?",
          name: "colabfromproject",
          component: () =>
            import("@/applications/nordicworkplace/nwp/views/customerSpecifics/sanovo/colabCreateView.vue"),
          props: route => ({
            projectId: route.params.projectId || undefined
          })
        }
      ]
    },

    // UCHI
    {
      path: "/uchis",
      component: RouterViewShell,
      children: [
        {
          path: "",
          name: "uchis",
          component: () => import("@/applications/nordicworkplace/nwp/views/UCHI/uchiView.vue"),
          props: true
        },
        {
          path: ":externalShareId?/edit/:tabId?/:itemId?",
          name: "uchi",
          component: () => import("@/applications/nordicworkplace/nwp/views/UCHI/uchiCreateView.vue"),
          props: route => ({
            externalShareId: route.params.externalShareId || undefined,
            tabId: route.params.tabId || undefined,
            itemId: route.params.itemId || undefined
          })
        },
        {
          path: ":driveId/:itemId?",
          name: "uchiShared",
          component: () => import("@/applications/nordicworkplace/nwp/views/UCHI/UchiFilelistView.vue"),
          props: route => ({
            driveId: route.params.driveId,
            itemId: route.params.itemId || undefined
          })
        }
      ]
    },

    {
      path: "/filehistory/:driveId/:itemId?",
      name: "uchiFileHistory",
      component: () => import("@/applications/nordicworkplace/nwp/views/UCHI/FileHistoryView.vue"),
      props: route => ({
        driveId: route.params.driveId,
        itemId: route.params.itemId || undefined
      })
    },

    {
      path: "/udocuments/:driveId?/:itemId?",
      name: "uchidocuments",
      component: () => import("@/applications/nordicworkplace/nwp/UchiDocumentsView.vue"),
      meta: { title: "Documents" },
      props: route => ({
        driveId: route.params.driveId || undefined,
        itemId: route.params.itemId || undefined,
        nctType: "uchi",
        nctSystem: "uchi",
        nctIdentifier: "uchi"
      })
    },
    {
      path: "/documents/:driveId?/:itemId?",
      name: "documents",
      component: () => import("@/views/Documents/DocumentsView.vue"),
      meta: { title: "Documents" },
      props: route => ({
        driveId: route.params.driveId || undefined,
        itemId: route.params.itemId || undefined
      })
    },

    {
      path: "/:pathMatch(.*)*",
      name: "not-found",
      component: () => import("@/views/NotFound.vue")
    }
  ]
});

nwpRouter.beforeEach((to, from, next) => {
  try {
    const sessionToken = SessionHelper.nctSessionToken();

    if (!sessionToken) {
      return;
    }

    let beforeEnterCalledNext = false;
    const beforeEnterNext = arg => {
      beforeEnterCalledNext = true;
      next(arg);
    };
    to.matched.at(-1)?.beforeEnter?.(to, from, beforeEnterNext);
    if (beforeEnterCalledNext) {
      return;
    }

    if (to.meta.roles) {
      const meStore = useMeStore();

      const allowedRoles = to.meta.roles.find(r => meStore[r]);

      if (!allowedRoles) {
        if (meStore.isSuperuser) {
          return next({ name: to.meta.redirects.isSuperuser, params: to.params });
        }

        if (meStore.isTeacher) {
          return next({ name: to.meta.redirects.isTeacher, params: to.params });
        }

        if (meStore.isStudent) {
          return next({ name: to.meta.redirects.isStudent, params: to.params });
        }
      }
    }

    const browserTitle = document.title.split(" - ")[0];

    if (browserTitle) {
      const nearestWithTitle = to.matched
        .slice()
        .reverse()
        .find(r => r.meta && r.meta.title);

      document.title =
        browserTitle + (nearestWithTitle && i18n ? " - " + i18n.global.t("Routes." + nearestWithTitle.meta.title) : "");
    }

    if (import.meta.env.PROD) {
      const activeInstitutionId = JwtHelper.getInstitutionId(SessionHelper.nctSessionToken());

      axios.post(
        "https://skobostats.azurewebsites.net/api/NavigationEvent",
        {
          UserId: useMeStore().userId,
          InstitutionId: activeInstitutionId,
          ToRoute: to.path,
          FromRoute: from.path,
          Role: useMeStore().isTeacher ? "teacher" : useMeStore().isSuperuser ? "superuser" : "student",
          UserAgent: navigator.userAgent,
          Language: navigator.language
        },
        { headers: { "x-functions-key": "eslMWIyl83a57fwj2ge3aG6LPqQyVKeTwH0ulpf4XSrDQVpeqwC4Ow==" } }
      );
    }

    next();
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log(e);
  }
});

nwpRouter.afterEach(to => {
  const settingsStore = useSettingsStore();
  settingsStore.logEndpoints.forEach(endpoint => {
    if (to.path.startsWith(endpoint)) {
      const meStore = useMeStore();
      meStore.logEvent(to.path);
    }
  });
});

/**
 * Set translation
 * @param { object } i i18n translation object
 */
export function setTranslater(i) {
  i18n = i;
}
